import { filterDojoOsRoles } from "../roles/dojoOsRoles.js";
import { getModulesForRole, DOJOOS_MODULES } from "../modules/dojoOsModules.js";

/**
 * @param {{ labelFr?: string, labelEn?: string, label?: string }} entry
 * @param {'fr'|'en'|string} locale
 */
export function getLocaleLabel(entry, locale = "fr") {
  if (!entry) return "";
  if (locale === "en") return entry.labelEn || entry.labelFr || entry.label || "";
  return entry.labelFr || entry.labelEn || entry.label || "";
}

/** Default route map — applications override via `routes`. */
export const DEFAULT_DOJOOS_ROUTES = {
  "admin.dashboard": "/admin",
  "admin.organizations": "/admin/organisations",
  "admin.dojos": "/admin/dojos",
  "admin.instructors": "/admin/instructeurs",
  "admin.assistants": "/admin/assistants",
  "admin.students": "/admin/etudiants",
  "admin.parents": "/admin/parents",
  "admin.classes": "/admin/classes",
  "admin.events": "/admin/evenements",
  "admin.documents": "/admin/documents",
  "admin.communications": "/admin/communications",
  "admin.accounts": "/admin/comptes",
  "admin.roles": "/admin/roles",
  "admin.requests": "/admin/demandes",
  "admin.import": "/admin/import",
  "admin.belts": "/admin/ceintures",
  "admin.competitions": "/admin/competitions",
  "admin.billing": "/admin/paiements",
  "admin.reports": "/admin/rapports",
  "manager.dashboard": "/sensei",
  "manager.dojos": "/mon-dojo",
  "manager.students": "/sensei/etudiants",
  "manager.classes": "/sensei/classes",
  "manager.events": "/sensei/evenements",
  "manager.documents": "/sensei/documents",
  "manager.progress": "/sensei/progression",
  "manager.exams": "/sensei/examens",
  "manager.attendance": "/sensei/presences",
  "manager.communications": "/sensei/communications",
  "manager.corrections": "/sensei/corrections",
  "assistant.classes": "/assistant/classes",
  "player.dashboard": "/mon-espace",
  "player.profile": "/mes-informations",
  "player.dojos": "/mon-dojo",
  "player.instructors": "/mon-sensei",
  "player.classes": "/mes-cours",
  "player.events": "/mes-cours",
  "player.progress": "/ma-progression",
  "player.documents": "/mes-documents",
  "player.schedule": "/mes-cours",
  "parent.dashboard": "/parent",
  "parent.children": "/parent/mes-enfants",
  "parent.schedule": "/parent/horaires",
  "parent.progress": "/parent/progression",
  "parent.events": "/parent/evenements",
  "parent.communications": "/parent/communications",
  "parent.documents": "/parent/documents",
  "common.help": "/aide",
  "common.training": "/formation",
  "public.home": "/",
  "public.about": "/a-propos",
  "public.dojos": "/dojos",
  "public.instructors": "/instructeurs",
  "public.students": "/etudiants",
  "public.documents": "/documents",
  "public.protocols": "/protocoles",
  "public.request": "/demande-compte",
  "public.login": "/connexion",
};

const NAV_ITEM_DEFS = {
  dashboard: { routeKey: (role) => `${roleNavPrefix(role)}.dashboard`, endAdmin: true },
  organizations: { routeKey: () => "admin.organizations" },
  dojos: { routeKey: (role) => (role === "admin" ? "admin.dojos" : role === "manager" ? "manager.dojos" : "player.dojos") },
  instructors: { routeKey: () => "admin.instructors" },
  assistants: { routeKey: () => "admin.assistants" },
  students: {
    routeKey: (role) =>
      role === "admin" ? "admin.students" : role === "manager" || role === "assistant" ? "manager.students" : "parent.children",
  },
  parents: { routeKey: () => "admin.parents" },
  classes: {
    routeKey: (role) =>
      role === "admin" ? "admin.classes" : role === "manager" || role === "assistant" ? "manager.classes" : "player.classes",
  },
  schedule: { routeKey: (role) => (role === "parent" ? "parent.schedule" : "player.schedule") },
  attendance: { routeKey: () => "manager.attendance" },
  belts: { routeKey: () => "admin.belts" },
  progress: {
    routeKey: (role) =>
      role === "manager" ? "manager.progress" : role === "parent" ? "parent.progress" : "player.progress",
  },
  exams: { routeKey: () => "manager.exams" },
  events: {
    routeKey: (role) =>
      role === "admin" ? "admin.events" : role === "manager" ? "manager.events" : role === "parent" ? "parent.events" : "player.events",
  },
  competitions: { routeKey: () => "admin.competitions" },
  documents: {
    routeKey: (role) =>
      role === "admin"
        ? "admin.documents"
        : role === "manager"
          ? "manager.documents"
          : role === "parent"
            ? "parent.documents"
            : "player.documents",
  },
  communications: {
    routeKey: (role) =>
      role === "admin" ? "admin.communications" : role === "manager" ? "manager.communications" : "parent.communications",
  },
  billing: { routeKey: () => "admin.billing" },
  reports: { routeKey: () => "admin.reports" },
  settings: { routeKey: () => "admin.accounts" },
  help: { routeKey: () => "common.help" },
  training: { routeKey: () => "common.training" },
};

function roleNavPrefix(dojoOsRoleId) {
  if (dojoOsRoleId === "admin") return "admin";
  if (dojoOsRoleId === "manager") return "manager";
  if (dojoOsRoleId === "assistant") return "assistant";
  if (dojoOsRoleId === "player") return "player";
  if (dojoOsRoleId === "parent") return "parent";
  return "admin";
}

/**
 * @param {string} dojoOsRoleId
 * @param {string[]} [enabledModules]
 * @param {Record<string, string>} routes
 */
export function buildDojoOsNavItemsForRole(dojoOsRoleId, enabledModules, routes) {
  const modules = getModulesForRole(dojoOsRoleId, enabledModules);
  const items = [];
  const seen = new Set();

  for (const mod of modules) {
    const def = NAV_ITEM_DEFS[mod.id];
    if (!def) continue;
    const routeKey = def.routeKey(dojoOsRoleId);
    const to = routes[routeKey];
    if (!to || seen.has(to)) continue;
    seen.add(to);
    items.push({
      id: `${dojoOsRoleId}-${mod.id}`,
      to,
      labelFr: mod.labelFr,
      labelEn: mod.labelEn,
      icon: mod.icon,
      moduleId: mod.id,
      phase: mod.phase,
      end: Boolean(def.endAdmin && dojoOsRoleId === "admin" && mod.id === "dashboard"),
    });
  }

  if (dojoOsRoleId === "admin") {
    for (const extra of [
      { id: "admin-roles", routeKey: "admin.roles", labelFr: "Rôles", labelEn: "Roles", icon: "🔑" },
      { id: "admin-requests", routeKey: "admin.requests", labelFr: "Demandes de compte", labelEn: "Account requests", icon: "✉️" },
      { id: "admin-import", routeKey: "admin.import", labelFr: "Import", labelEn: "Import", icon: "📥" },
    ]) {
      const to = routes[extra.routeKey];
      if (to && !seen.has(to)) {
        seen.add(to);
        items.push({ ...extra, to, moduleId: "settings", phase: "phase1" });
      }
    }
  }

  if (dojoOsRoleId === "manager") {
    const corrections = routes["manager.corrections"];
    if (corrections && !seen.has(corrections)) {
      items.push({
        id: "manager-corrections",
        to: corrections,
        labelFr: "Corrections / demandes",
        labelEn: "Reviews",
        icon: "✅",
        moduleId: "communications",
        phase: "phase1",
      });
    }
  }

  return items;
}

const NAV_GROUP_META = {
  admin: { id: "admin", labelFr: "Administration", labelEn: "Administration", dojoOsRoles: ["admin"] },
  manager: { id: "sensei", labelFr: "Espace Sensei", labelEn: "Sensei space", dojoOsRoles: ["manager"] },
  assistant: { id: "assistant", labelFr: "Espace Sempai", labelEn: "Sempai space", dojoOsRoles: ["assistant"] },
  player: { id: "student", labelFr: "Mon espace", labelEn: "My space", dojoOsRoles: ["player"] },
  parent: { id: "parent", labelFr: "Espace Parent", labelEn: "Parent space", dojoOsRoles: ["parent"] },
};

/**
 * @param {object} options
 * @param {string} options.appName
 * @param {string} [options.appShortName]
 * @param {'demo'|'production'} [options.mode]
 * @param {string[]} [options.enabledRoles]
 * @param {string[]} [options.enabledModules]
 * @param {Record<string, string>} [options.routes]
 * @param {Record<string, string>} [options.roleIdMap]
 * @param {Record<string, string[]>} [options.roleAliases] — app role ids per DojoOS role
 * @param {Array} [options.publicNavItems]
 * @param {object} [options.demoBanner]
 * @param {object} [options.phaseLabel]
 */
export function createDojoOsAppConfig(options) {
  const {
    appName,
    appShortName = appName,
    mode = "demo",
    enabledRoles,
    enabledModules,
    routes: routesOverride,
    roleIdMap = {},
    roleAliases = {},
    publicNavItems,
    demoBanner = {
      fr: "Données [DÉMO] — aucune donnée officielle",
      en: "[DEMO] data — no official records",
    },
    phaseLabel = { fr: "Phase 1", en: "Phase 1" },
  } = options;

  const routes = { ...DEFAULT_DOJOOS_ROUTES, ...routesOverride };
  const roles = filterDojoOsRoles(enabledRoles).map((r) => ({
    id: roleIdMap[r.id] || r.id,
    dojoOsId: r.id,
    labelFr: r.labelFr,
    labelEn: r.labelEn,
    icon: r.icon,
    descriptionFr: r.descriptionFr,
    descriptionEn: r.descriptionEn,
    availability: r.availability,
    accessLevel: r.accessLevel,
  }));

  const guestRole = { id: "guest", labelFr: "Visiteur", labelEn: "Guest", icon: "🌐", dojoOsId: "guest" };
  const allRoles = [guestRole, ...roles.filter((r) => r.id !== "guest")];

  const defaultPublic = [
    { id: "home", to: routes["public.home"], labelFr: "Accueil", labelEn: "Home", icon: "🏠", end: true },
    { id: "about", to: routes["public.about"], labelFr: "À propos", labelEn: "About", icon: "ℹ️" },
    { id: "dojos", to: routes["public.dojos"], labelFr: "Dojos", labelEn: "Dojos", icon: "🥋" },
    { id: "instructors", to: routes["public.instructors"], labelFr: "Instructeurs", labelEn: "Instructors", icon: "👊" },
    { id: "students-pub", to: routes["public.students"], labelFr: "Étudiants", labelEn: "Students", icon: "🎓" },
    { id: "documents", to: routes["public.documents"], labelFr: "Documents", labelEn: "Documents", icon: "📄" },
    { id: "protocols", to: routes["public.protocols"], labelFr: "Protocoles", labelEn: "Protocols", icon: "📋" },
    { id: "request-account", to: routes["public.request"], labelFr: "Demander un compte", labelEn: "Request account", icon: "✉️" },
    { id: "login", to: routes["public.login"], labelFr: "Connexion", labelEn: "Log in", icon: "🔐" },
  ];

  const navGroups = [
    {
      id: "public",
      labelFr: "Public",
      labelEn: "Public",
      items: publicNavItems || defaultPublic,
    },
  ];

  for (const [key, meta] of Object.entries(NAV_GROUP_META)) {
    if (enabledRoles?.length && !meta.dojoOsRoles.some((r) => enabledRoles.includes(r))) continue;
    const dojoOsRoleId = meta.dojoOsRoles[0];
    const items = buildDojoOsNavItemsForRole(dojoOsRoleId, enabledModules, routes);
    if (!items.length) continue;
    const appRoleIds =
      roleAliases[dojoOsRoleId]?.length > 0
        ? roleAliases[dojoOsRoleId]
        : [roleIdMap[dojoOsRoleId] || dojoOsRoleId];
    navGroups.push({
      id: meta.id,
      labelFr: meta.labelFr,
      labelEn: meta.labelEn,
      roles: appRoleIds,
      items,
    });
  }

  return {
    appName,
    appShortName,
    mode,
    defaultLocale: "fr",
    supportedLocales: ["fr", "en"],
    phaseLabel,
    demoBanner,
    foundation: "dojoos",
    roles: allRoles,
    navGroups,
    dojoOs: {
      enabledRoles: enabledRoles || filterDojoOsRoles().map((r) => r.id),
      enabledModules: enabledModules || DOJOOS_MODULES.map((m) => m.id),
      routes,
      roleIdMap,
      roleAliases,
    },
  };
}

/**
 * @param {ReturnType<typeof createDojoOsAppConfig>} config
 * @param {string} navGroupId
 * @param {'fr'|'en'|string} [locale]
 */
export function resolveDojoOsNavItems(config, navGroupId, locale = "fr") {
  const group = config.navGroups.find((g) => g.id === navGroupId);
  if (!group) return [];
  return (group.items || []).map((item) => ({
    ...item,
    label: getLocaleLabel(item, locale),
  }));
}
